import { FC, useMemo, useRef } from "react";
import {
  MuiBox,
  MuiDoubleLabel,
  MuiGrid,
  MuiLink,
  MuiMultiSelect,
  MuiTypography,
} from "../../../../../../../components";
import MuiChip from "../../../../../../../components/chip/MuiChip";
import { MuiDoubleLabelWithChip } from "../../../../../../../components/doubleLabelWithChip/MuiDoubleLabelWithChip";
import {
  StatusBadge,
  StatusBadgeType,
} from "../../../../../../../components/statusBadge/StatusBadge";
import { EMPTY_VALUE_DASH } from "../../../../../../../helpers/constants";
import { isNotEmpty } from "../../../../../../../helpers/utils";
import { useMultiSelectARIALabels } from "../../../../../../../hooks/useMultiSelectARIALabels";
import { useNavigationSiteCore } from "../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../hooks/useSiteCore";
import { activitiesAndDeliverablesSiteCoreKeys } from "../../../../../siteCore/SiteCoreKeys";
import { ViewActivityResponse } from "../../../modelsAndSchemas/models";

interface ViewActivityProps {
  activityData: ViewActivityResponse;
  children?: React.ReactNode;
  onViewManageLocal: () => void;
}

export const ViewActivityGeneral: FC<ViewActivityProps> = ({
  activityData,
  children,
  onViewManageLocal,
}) => {
  const candidateStatusRef = useRef<HTMLDivElement>(null);
  const modalityRef = useRef<HTMLDivElement>(null);
  const gridSize = { md: 4, sm: 6, xs: 12 };
  const { fetchValueByKeyOfDropdown, fetchLabel, commonLabels } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const multiSelectARIALabels = useMultiSelectARIALabels();
  const activityStatusLabel = fetchValueByKeyOfDropdown(
    activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
    activitiesAndDeliverablesSiteCoreKeys.activityStatusList,
    activityData?.activityStatus ?? "",
  );

  const renderChip = (value: string) => {
    return (
      <MuiChip
        data={[
          {
            value: value,
            label: fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              value,
            ),
          },
        ]}
        handleDelete={() => {}}
        isEditable={false}
        removeChipNameARIALabel={commonARIALabel.removeChipName}
        showMoreARIALabel={commonARIALabel.showNMore}
      />
    );
  };

  const deliverablesOption = useMemo(() => {
    return Array.isArray(activityData?.deliverables)
      ? activityData.deliverables.map((item) => ({
          label: item.name,
          value: item.name,
        }))
      : [];
  }, [activityData]);

  const modalityChipsValue = useMemo(() => {
    if (!activityData?.modalities?.length) {
      return [];
    }
    return activityData.modalities.map((modalitySiteCoreValue: string) => ({
      label: fetchValueByKeyOfDropdown(
        activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
        activitiesAndDeliverablesSiteCoreKeys.modalityList,
        modalitySiteCoreValue,
      ),
      value: modalitySiteCoreValue,
    }));
  }, [activityData?.modalities]);

  return (
    <MuiBox>
      <MuiGrid
        container
        data-testid="activity-tab-general"
        className="activity-tab-general"
      >
        <MuiGrid size={gridSize}>
          <MuiDoubleLabelWithChip
            label={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.participantStatus,
            )}
            replaceEmptyWithDash={!activityData?.candidateStatuses?.length}
            value={activityData?.candidateStatuses?.map((item: string) => ({
              label: fetchValueByKeyOfDropdown(
                activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                activitiesAndDeliverablesSiteCoreKeys.participantStatusList,
                item,
              ),
              value: item,
            }))}
            chipPopoverHeaderLabel={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.participantStatus,
            )}
            chipPopoverGridRef={candidateStatusRef}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.activityType,
            )}
            value={activityData?.activityType?.name ?? ""}
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabelWithChip
            label={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.modality,
            )}
            replaceEmptyWithDash={!activityData?.modalities?.length}
            value={modalityChipsValue}
            chipPopoverGridRef={modalityRef}
            chipPopoverHeaderLabel={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.modality,
            )}
            showMoreARIALabel={commonARIALabel.showNMore}
            removeChipNameARIALabel={commonARIALabel.removeChipName}
            closePopoverARIALabel={commonARIALabel.closePopover}
          />
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.topic,
            )}
            value={activityData?.topicName ?? ""}
            isValueWithEllipsis
            valueClasses="topic-value"
          />
          <MuiLink className="view-locale-link" onClick={onViewManageLocal}>
            {commonLabels.viewLocale}
          </MuiLink>
        </MuiGrid>
        <MuiGrid size={gridSize} className="deliverables-dropdown">
          <MuiDoubleLabel
            label={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.deliverables,
            )}
            value={
              isNotEmpty(activityData?.deliverables) &&
              deliverablesOption.length
                ? ""
                : EMPTY_VALUE_DASH
            }
          />
          {isNotEmpty(activityData?.deliverables) &&
            !!deliverablesOption.length && (
              <MuiMultiSelect
                label={""}
                options={deliverablesOption}
                showChip={true}
                showDropdown={false}
                isChipEditable={false}
                dialogTitle={fetchLabel(
                  activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                  activitiesAndDeliverablesSiteCoreKeys.selectedDeliverables,
                )}
                dialogWidth={376}
                selectedValues={
                  activityData?.deliverables
                    ? activityData.deliverables.map((item) => item.name)
                    : []
                }
                selectPlaceHolder={commonLabels.selectPlaceHolder}
                searchPlaceHolder={commonLabels.search}
                selectAllPlaceHolder={commonLabels.selectAllPlaceHolder}
                doneText={commonLabels.done}
                allARIAProps={multiSelectARIALabels}
              />
            )}
        </MuiGrid>
        <MuiGrid size={gridSize}>
          <MuiDoubleLabel
            label={fetchLabel(
              activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
              activitiesAndDeliverablesSiteCoreKeys.activityStatus,
            )}
            value={""}
          />
          <StatusBadge
            status={activityData?.activityStatus as StatusBadgeType}
            label={activityStatusLabel}
          />
        </MuiGrid>
      </MuiGrid>

      <MuiBox>
        <MuiTypography
          variant="h2Bold"
          className="select-applicable default-activity-value"
          component="h2"
        >
          {fetchLabel(
            activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
            activitiesAndDeliverablesSiteCoreKeys.defaultActivityValues,
          )}
        </MuiTypography>
      </MuiBox>
      {children}

      {(activityData?.triggerBilling ||
        activityData?.countsTowardsLastContactDate ||
        activityData?.triggerTouchPointSurvey) && (
        <>
          <MuiBox>
            <MuiTypography
              variant="h2Bold"
              className="select-applicable"
              component="h2"
            >
              {fetchLabel(
                activitiesAndDeliverablesSiteCoreKeys.createActivityKey,
                activitiesAndDeliverablesSiteCoreKeys.selectedOptionsText,
              )}
            </MuiTypography>
          </MuiBox>
          <MuiBox className="checkbox-container">
            {activityData?.triggerBilling &&
              renderChip(activitiesAndDeliverablesSiteCoreKeys.triggerBilling)}
            {activityData?.countsTowardsLastContactDate &&
              renderChip(
                activitiesAndDeliverablesSiteCoreKeys.countsTowardsLastContactDate,
              )}
            {activityData?.triggerTouchPointSurvey &&
              renderChip(
                activitiesAndDeliverablesSiteCoreKeys.triggerTouchPointSurvey,
              )}
          </MuiBox>
        </>
      )}
    </MuiBox>
  );
};
